// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useCallback } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

const TOUR_SEEN_KEY = 'jw_audit_tour_seen';

export function useTour(isAdmin = false) {
  const hasSeenTour = () => localStorage.getItem(TOUR_SEEN_KEY) === 'true';

  const markTourSeen = () => localStorage.setItem(TOUR_SEEN_KEY, 'true');

  const resetTour = () => localStorage.removeItem(TOUR_SEEN_KEY);

  const startTour = useCallback(() => {
    const baseSteps = [
      {
        popover: {
          title: 'Bem-vindo ao JW Audit!',
          description: 'Vamos fazer um tour rápido para você conhecer as principais funcionalidades do sistema. Clique em "Próximo" para começar.',
        },
      },
      {
        element: '#tour-area-selector',
        popover: {
          title: 'Área de Trabalho',
          description: 'Aqui você pode ver em qual área está trabalhando (Congregação ou Manutenção). Clique para trocar de área a qualquer momento.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '#tour-nav-menu',
        popover: {
          title: 'Menu de Navegação',
          description: 'Use o menu lateral para navegar entre as seções do sistema.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '#tour-nav-dashboard',
        popover: {
          title: 'Visão Geral',
          description: 'Veja um resumo das suas atividades: total de arquivos, espaço utilizado e atividades recentes.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '#tour-nav-documents',
        popover: {
          title: 'Documentos',
          description: 'Gerencie todos os seus documentos aqui. Você pode fazer upload, criar pastas, visualizar, renomear, mover e excluir arquivos.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '#tour-nav-audit',
        popover: {
          title: 'S-25b Auditoria',
          description: 'Seção dedicada para auditoria trimestral. Crie relatórios S-25b, preencha formulários e consulte documentos ao mesmo tempo.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '#tour-theme-toggle',
        popover: {
          title: 'Tema Claro / Escuro',
          description: 'Alterne entre o tema claro e escuro conforme sua preferência.',
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '#tour-notifications',
        popover: {
          title: 'Notificações',
          description: 'Fique por dentro das novidades e atividades recentes do sistema.',
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '#tour-user-menu',
        popover: {
          title: 'Menu do Usuário',
          description: 'Acesse suas configurações pessoais, troque de área ou saia do sistema.',
          side: 'bottom',
          align: 'end',
        },
      },
    ];

    const adminSteps = [
      {
        element: '#tour-admin-section',
        popover: {
          title: 'Administração',
          description: 'Como administrador, você tem acesso a funcionalidades extras.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '#tour-admin-users',
        popover: {
          title: 'Gerenciar Usuários',
          description: 'Adicione, edite permissões, promova ou remova usuários do sistema.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '#tour-admin-logs',
        popover: {
          title: 'Logs de Auditoria',
          description: 'Acompanhe todas as ações realizadas no sistema, com filtros por usuário, ação e data.',
          side: 'right',
          align: 'start',
        },
      },
    ];

    const finalStep = {
      popover: {
        title: 'Tudo Pronto!',
        description: 'Agora você já conhece o sistema. Clique no botão de ajuda (?) no cabeçalho a qualquer momento para rever este tour. Bom trabalho!',
      },
    };

    const steps = [
      ...baseSteps,
      ...(isAdmin ? adminSteps : []),
      finalStep,
    ];

    const driverObj = driver({
      showProgress: true,
      animate: true,
      allowClose: true,
      overlayColor: 'rgba(0, 0, 0, 0.6)',
      stagePadding: 8,
      stageRadius: 12,
      popoverClass: 'jw-tour-popover',
      progressText: '{{current}} de {{total}}',
      nextBtnText: 'Próximo',
      prevBtnText: 'Anterior',
      doneBtnText: 'Concluir',
      steps,
      onDestroyed: () => {
        markTourSeen();
      },
    });

    driverObj.drive();

    return driverObj;
  }, [isAdmin]);

  return { startTour, hasSeenTour, markTourSeen, resetTour };
}
